import api from './api';
import type {
  Analysis,
  AnalysisHistoryEntry,
  AnalysisRun,
  AnalysisSummaryPerDepot,
  HardnessProfile,
  TriggerAnalysisRequest,
} from '../types';

export const analysesService = {
  async list(filters?: { aoId?: string; depotId?: string; decision?: string; runId?: string }): Promise<Analysis[]> {
    const params = new URLSearchParams();
    if (filters?.aoId) params.append('aoId', filters.aoId);
    if (filters?.depotId) params.append('depotId', filters.depotId);
    if (filters?.decision) params.append('decision', filters.decision);
    if (filters?.runId) params.append('runId', filters.runId);

    const { data } = await api.get<{ success: boolean; data: { analyses: Analysis[] } }>(
      `/analyses?${params.toString()}`
    );
    return data.data.analyses;
  },

  async getAll(): Promise<Analysis[]> {
    return this.list();
  },

  async getByAoId(aoId: string): Promise<Analysis[]> {
    return this.list({ aoId });
  },

  async getById(id: string): Promise<Analysis> {
    const { data } = await api.get<{ success: boolean; data: Analysis }>(`/analyses/${id}`);
    return data.data;
  },

  async getByDepotId(depotId: string): Promise<Analysis | null> {
    const { data } = await api.get<{ success: boolean; data: Analysis | null }>(
      `/depots/${depotId}/analysis`
    );
    return data.data;
  },

  // Déclencher une analyse pour un dépôt spécifique
  async trigger(request: TriggerAnalysisRequest): Promise<Analysis> {
    const { data } = await api.post<{ success: boolean; data: { analysis: Analysis } }>(
      '/analyses/trigger',
      request
    );
    return data.data.analysis;
  },

  async analyzeCv(request: {
    depotId: string;
    mode?: 'anonymized_gemini' | 'infomaniak_dual';
    model?: string;
    infomaniakVisionModel?: 'kimi' | 'qwen' | 'mistral';
    infomaniakAnalysisModel?: string;
    hardness?: HardnessProfile;
  }): Promise<Analysis> {
    const { data } = await api.post<{ success: boolean; data: { analysis: Analysis } }>(
      '/analyses/analyze-cv',
      request
    );
    return data.data.analysis;
  },

  // Déclencher les analyses pour tous les dépôts d'un AO
  async triggerBatch(
    aoId: string,
    reanalyze: boolean = false,
    options?: {
      mode?: 'anonymized_gemini' | 'infomaniak_dual';
      model?: string;
      infomaniakVisionModel?: 'kimi' | 'qwen' | 'mistral';
      infomaniakAnalysisModel?: string;
      hardness?: HardnessProfile;
    }
  ): Promise<{ successCount: number; errorCount: number; runId?: string }> {
    const { data } = await api.post<{
      success: boolean;
      data: { successCount: number; errorCount: number; runId?: string };
    }>('/analyses/trigger-batch', { aoId, reanalyze, ...options });
    return data.data;
  },

  async listRuns(aoId: string): Promise<AnalysisRun[]> {
    const { data } = await api.get<{ success: boolean; data: { runs: AnalysisRun[] } }>(
      `/analyses/runs?aoId=${aoId}`
    );
    return data.data.runs;
  },

  // Relancer une analyse
  async retry(id: string): Promise<Analysis> {
    const { data } = await api.post<{ success: boolean; data: { analysis: Analysis } }>(
      `/analyses/${id}/retry`
    );
    return data.data.analysis;
  },

  async listLatestPerDepot(aoId?: string): Promise<AnalysisSummaryPerDepot[]> {
    const params = new URLSearchParams();
    if (aoId) params.append('aoId', aoId);
    const { data } = await api.get<{ success: boolean; data: { analyses: AnalysisSummaryPerDepot[] } }>(
      `/analyses/latest-per-depot?${params.toString()}`
    );
    return data.data.analyses;
  },

  async getHistory(depotId: string): Promise<AnalysisHistoryEntry[]> {
    const { data } = await api.get<{ success: boolean; data: { history: AnalysisHistoryEntry[] } }>(
      `/analyses/history/${depotId}`
    );
    return data.data.history;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/analyses/${id}`);
  },

  async update(id: string, updateData: Partial<Pick<Analysis, 'rating' | 'isExcluded' | 'comment'>>): Promise<Analysis> {
    const { data } = await api.put<{ success: boolean; data: Analysis }>(
      `/analyses/${id}`,
      updateData
    );
    return data.data;
  },
};

export default analysesService;
